/**
 * ConnectionStateOverlay — FE-014: Connection State UI
 *
 * Overlay components for all connection states:
 * - Connecting/Finding: own video preview + "Finding someone..."
 * - Matched: brief celebration animation
 * - Reconnecting: "Connection lost, reconnecting..."
 * - Partner Left: "Your chat partner left." with Find Next button
 * - Error: user-friendly error messages
 * - No Match Timeout: low traffic options after 30s
 *
 * All states have smooth enter/exit transitions.
 * Never leaves user in indefinite loading without feedback.
 */
import {
  type Component,
  createSignal,
  createEffect,
  onCleanup,
  Show,
} from 'solid-js';
import type { SessionState, SessionError, BanInfo, RateLimitInfo } from '../services/session-manager';
import { IconSearch, IconCheck, IconWifiOff, IconWarning, IconUser, IconBell } from './Icons';
import styles from './ConnectionStateOverlay.module.css';

export interface ConnectionStateOverlayProps {
  /** Current session state */
  sessionState: () => SessionState;
  /** Session error info */
  error: () => SessionError | null;
  /** Whether the peer left */
  peerLeft: () => boolean;
  /** Ban info if banned */
  banInfo: () => BanInfo | null;
  /** Rate limit info */
  rateLimitInfo: () => RateLimitInfo | null;
  /** Callback: find next person */
  onNext: () => void;
  /** Callback: end session and go home */
  onEnd: () => void;
}

/** Map error codes to user-friendly messages */
function friendlyErrorMessage(error: SessionError | null): string {
  if (!error) return 'Something went wrong. Please try again.';

  switch (error.code) {
    case 'CONNECTION_FAILED':
      return 'Could not connect to the server. Check your internet connection and try again.';
    case 'OFFER_FAILED':
    case 'ANSWER_FAILED':
    case 'ANSWER_HANDLE_FAILED':
      return 'Could not establish a video connection. This might be a network issue. Try switching from WiFi to mobile data, or tap Try Again.';
    case 'SIGNALING_DISCONNECTED':
      return 'Lost connection to the server. Please check your internet and try again.';
    case 'PEER_CONNECTION_FAILED':
      return 'The video connection dropped. This can happen on unstable networks. Tap Try Again.';
    case 'TIMEOUT':
      return 'The connection timed out. Please try again.';
    case 'ROOM_FULL':
      return 'This chat room is no longer available. Finding a new match...';
    case 'INVALID_STATE':
      return 'Something unexpected happened. Please refresh the page and try again.';
    default:
      return 'We hit a bump in the road. Please try again.';
  }
}

const ConnectionStateOverlay: Component<ConnectionStateOverlayProps> = (props) => {
  // Timer for no-match timeout (30 seconds in queue)
  const [showTimeout, setShowTimeout] = createSignal(false);
  const [waitSeconds, setWaitSeconds] = createSignal(0);
  let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  let waitInterval: ReturnType<typeof setInterval> | null = null;

  // Track matched celebration state
  const [showMatchCelebration, setShowMatchCelebration] = createSignal(false);

  // When entering 'waiting' state, start the no-match timer
  createEffect(() => {
    const state = props.sessionState();

    // Clear previous timers
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }
    if (waitInterval) {
      clearInterval(waitInterval);
      waitInterval = null;
    }
    setShowTimeout(false);
    setWaitSeconds(0);

    if (state === 'waiting') {
      // Count wait time
      waitInterval = setInterval(() => {
        setWaitSeconds((s) => s + 1);
      }, 1000);

      // Show timeout after 30 seconds
      timeoutTimer = setTimeout(() => {
        setShowTimeout(true);
      }, 30000);
    }

    // Show celebration when matched
    if (state === 'matched') {
      setShowMatchCelebration(true);
      setTimeout(() => setShowMatchCelebration(false), 1500);
    }
  });

  onCleanup(() => {
    if (timeoutTimer) clearTimeout(timeoutTimer);
    if (waitInterval) clearInterval(waitInterval);
  });

  const isWaiting = () => props.sessionState() === 'waiting';
  const isMatched = () => props.sessionState() === 'matched';
  const isConnecting = () => props.sessionState() === 'connecting';
  const isEnded = () => props.sessionState() === 'ended';
  const isJoining = () => props.sessionState() === 'joining';

  const shouldShowOverlay = () =>
    isWaiting() || isMatched() || isConnecting() || isJoining() || isEnded();

  return (
    <Show when={shouldShowOverlay()}>
      <div
        class={`${styles.overlay} ${isEnded() ? styles.overlayDark : ''}`}
        role="status"
        aria-live="polite"
        aria-label="Connection status"
      >
        {/* ============ JOINING STATE ============ */}
        <Show when={isJoining()}>
          <div class={styles.stateContainer}>
            <div class={styles.spinnerLarge} aria-hidden="true" />
            <p class={styles.stateTitle}>Connecting to server...</p>
            <p class={styles.stateSubtitle}>Just a moment</p>
          </div>
        </Show>

        {/* ============ WAITING / FINDING STATE ============ */}
        <Show when={isWaiting() && !showTimeout()}>
          <div class={styles.stateContainer}>
            <div class={styles.searchAnimation} aria-hidden="true">
              <IconSearch size={40} />
              <div class={styles.searchRing} />
              <div class={styles.searchRing2} />
            </div>
            <p class={styles.stateTitle}>
              Finding someone
              <span class={styles.dots}>
                <span class={styles.dot}>.</span>
                <span class={styles.dot}>.</span>
                <span class={styles.dot}>.</span>
              </span>
            </p>
            <p class={styles.stateSubtitle}>
              {waitSeconds() > 5
                ? `Searching for ${waitSeconds()} seconds`
                : 'This usually takes a few seconds'}
            </p>
          </div>
        </Show>

        {/* ============ NO MATCH TIMEOUT STATE ============ */}
        <Show when={isWaiting() && showTimeout()}>
          <div class={styles.stateContainer}>
            <div class={styles.timeoutIcon} aria-hidden="true">
              <IconUser size={48} />
            </div>
            <p class={styles.stateTitle}>Low traffic right now</p>
            <p class={styles.stateSubtitle}>
              Fewer people are online at this time. You can keep waiting or come back during peak hours.
            </p>
            <div class={styles.timeoutActions}>
              <button
                class={styles.primaryAction}
                onClick={() => {
                  setShowTimeout(false);
                  setWaitSeconds(0);
                  // Reset the timeout timer for another 30s
                  if (timeoutTimer) clearTimeout(timeoutTimer);
                  timeoutTimer = setTimeout(() => setShowTimeout(true), 30000);
                }}
                type="button"
              >
                Keep Waiting
              </button>
              <button
                class={styles.secondaryAction}
                onClick={() => {
                  props.onEnd();
                }}
                type="button"
              >
                <IconBell size={16} />
                <span>Notify Me at Peak Time</span>
              </button>
            </div>
          </div>
        </Show>

        {/* ============ MATCHED CELEBRATION ============ */}
        <Show when={isMatched() || (isConnecting() && showMatchCelebration())}>
          <div class={`${styles.stateContainer} ${styles.celebration}`}>
            <div class={styles.celebrationIcon} aria-hidden="true">
              <IconCheck size={48} />
            </div>
            <p class={styles.stateTitle}>Matched!</p>
            <p class={styles.stateSubtitle}>Connecting you now...</p>
          </div>
        </Show>

        {/* ============ CONNECTING STATE ============ */}
        <Show when={isConnecting() && !showMatchCelebration()}>
          <div class={styles.stateContainer}>
            <div class={styles.spinnerLarge} aria-hidden="true" />
            <p class={styles.stateTitle}>Establishing connection...</p>
            <p class={styles.stateSubtitle}>Setting up your video call</p>
          </div>
        </Show>

        {/* ============ ENDED — PEER LEFT ============ */}
        <Show when={isEnded() && props.peerLeft() && !props.error()}>
          <div class={styles.stateContainer}>
            <div class={styles.leftIcon} aria-hidden="true">
              <IconUser size={48} />
            </div>
            <p class={styles.stateTitle}>Your chat partner left</p>
            <p class={styles.stateSubtitle}>
              It happens. Ready to meet someone new?
            </p>
            <div class={styles.endedActions}>
              <button
                class={styles.primaryAction}
                onClick={props.onNext}
                type="button"
              >
                Find Next
              </button>
              <button
                class={styles.ghostAction}
                onClick={props.onEnd}
                type="button"
              >
                Back to Home
              </button>
            </div>
          </div>
        </Show>

        {/* ============ ENDED — ERROR ============ */}
        <Show when={isEnded() && props.error() && !props.banInfo()}>
          <div class={styles.stateContainer}>
            <div class={styles.errorIcon} aria-hidden="true">
              <IconWifiOff size={48} />
            </div>
            <p class={styles.stateTitle}>Connection Issue</p>
            <p class={styles.stateSubtitle}>
              {friendlyErrorMessage(props.error())}
            </p>
            <div class={styles.endedActions}>
              <button
                class={styles.primaryAction}
                onClick={props.onNext}
                type="button"
              >
                Try Again
              </button>
              <button
                class={styles.ghostAction}
                onClick={props.onEnd}
                type="button"
              >
                Back to Home
              </button>
            </div>
          </div>
        </Show>

        {/* ============ ENDED — BANNED ============ */}
        <Show when={isEnded() && props.banInfo()}>
          <div class={styles.stateContainer}>
            <div class={styles.errorIcon} aria-hidden="true">
              <IconWarning size={48} />
            </div>
            <p class={styles.stateTitle}>Account Restricted</p>
            <p class={styles.stateSubtitle}>
              Your access has been temporarily restricted due to: {props.banInfo()?.reason}.
              {props.banInfo()?.expiresAt && (
                <> This restriction expires at {new Date(props.banInfo()!.expiresAt).toLocaleString()}.</>
              )}
            </p>
            <div class={styles.endedActions}>
              <button
                class={styles.ghostAction}
                onClick={props.onEnd}
                type="button"
              >
                Back to Home
              </button>
            </div>
          </div>
        </Show>

        {/* ============ ENDED — RATE LIMITED ============ */}
        <Show when={isEnded() && props.rateLimitInfo() && !props.banInfo() && !props.error()}>
          <div class={styles.stateContainer}>
            <div class={styles.errorIcon} aria-hidden="true">
              <IconWarning size={48} />
            </div>
            <p class={styles.stateTitle}>Slow Down</p>
            <p class={styles.stateSubtitle}>
              You are skipping too quickly. Please wait{' '}
              {Math.ceil((props.rateLimitInfo()?.retryAfterMs ?? 5000) / 1000)} seconds before trying again.
            </p>
            <div class={styles.endedActions}>
              <button
                class={styles.ghostAction}
                onClick={props.onEnd}
                type="button"
              >
                Back to Home
              </button>
            </div>
          </div>
        </Show>

        {/* ============ ENDED — GENERIC (no peer left, no error) ============ */}
        <Show when={isEnded() && !props.peerLeft() && !props.error() && !props.banInfo() && !props.rateLimitInfo()}>
          <div class={styles.stateContainer}>
            <p class={styles.stateTitle}>Session ended</p>
            <div class={styles.endedActions}>
              <button
                class={styles.primaryAction}
                onClick={props.onNext}
                type="button"
              >
                Find Next
              </button>
              <button
                class={styles.ghostAction}
                onClick={props.onEnd}
                type="button"
              >
                Back to Home
              </button>
            </div>
          </div>
        </Show>
      </div>
    </Show>
  );
};

export default ConnectionStateOverlay;
